import { useEffect, useState } from 'react';
import type { ExecutionMode, ReviewMode, RunStageName, WorkflowType } from '@orqforge/shared';
import { runStageNames } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';

const STAGES: RunStageName[] = [...runStageNames];
const ALWAYS_ON: RunStageName[] = ['Execute', 'Done'];
const EXECUTION_MODES: ExecutionMode[] = ['assist', 'draft', 'auto'];
const REVIEW_MODES: ReviewMode[] = ['none', 'ai', 'human'];

function Segments<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: T[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={`rounded-lg px-3 py-1.5 text-sm ${value === option ? 'bg-blue-600 font-semibold text-white' : 'text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-700'}`}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export function WorkflowTypesPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const [types, setTypes] = useState<WorkflowType[]>([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [executionMode, setExecutionMode] = useState<ExecutionMode>('assist');
  const [reviewMode, setReviewMode] = useState<ReviewMode>('human');
  const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
  const [enabled, setEnabled] = useState<Set<RunStageName>>(new Set(STAGES));
  const [pendingId, setPendingId] = useState<string | null>(null);

  function stagesFor(t: WorkflowType): RunStageName[] {
    return t.enabledStages.length ? t.enabledStages : STAGES;
  }

  function isAlwaysOn(stage: RunStageName) {
    return ALWAYS_ON.includes(stage);
  }

  function isEnabled(stage: RunStageName) {
    return isAlwaysOn(stage) || enabled.has(stage);
  }

  function toggle(stage: RunStageName) {
    if (isAlwaysOn(stage)) return;
    setEnabled((current) => {
      const next = new Set(current);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }

  function resetForm() {
    setEditingId(null);
    setKey('');
    setName('');
    setExecutionMode('assist');
    setReviewMode('human');
    setConfidenceThreshold(0.7);
    setEnabled(new Set(STAGES));
  }

  async function load() {
    if (!workspaceId) return;
    setTypes(await apiClient.listWorkflowTypes());
  }

  useEffect(() => {
    void load().catch(() => setTypes([]));
  }, [workspaceId]);

  function edit(t: WorkflowType) {
    setEditingId(t.id);
    setKey(t.key);
    setName(t.name);
    setExecutionMode(t.executionMode);
    setReviewMode(t.reviewMode);
    setConfidenceThreshold(t.confidenceThreshold);
    setEnabled(new Set(stagesFor(t)));
  }

  async function save() {
    if (saving || !key.trim() || !name.trim()) return;
    setSaving(true);
    const enabledStages = STAGES.filter((s) => isAlwaysOn(s) || enabled.has(s));
    const payload = {
      name: name.trim(),
      executionMode,
      reviewMode,
      enabledStages,
      reviewerRoles: [] as string[],
      confidenceThreshold: Number(confidenceThreshold) || 0.7,
    };
    try {
      if (editingId) await apiClient.updateWorkflowType(editingId, payload);
      else await apiClient.createWorkflowType({ key: key.trim(), ...payload });
      resetForm();
      await load();
    } catch {
      /* keep the form */
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    await apiClient.deleteWorkflowType(id);
    setPendingId(null);
    if (editingId === id) resetForm();
    await load();
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold">Workflow Types</h1>
      <p className="text-sm text-gray-500">
        Per-type execution mode, review mode, and enabled pipeline stages for{' '}
        <strong>{workspace?.name || 'no workspace'}</strong>.
      </p>

      {!workspaceId ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          Select a workspace from the sidebar to manage its workflow types.
        </div>
      ) : (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-3 lg:col-span-2">
            {types.length === 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
                No workflow types yet. Add one to drive per-type stages and review behaviour. A type keyed <code>default</code> applies to runs with no explicit type.
              </div>
            )}
            {types.map((t) => (
              <div key={t.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-start justify-between">
                  <div>
                    <strong>{t.name}</strong>
                    <code className="ml-2 text-xs text-gray-500">{t.key}</code>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => edit(t)}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setPendingId(t.id)}>Delete</Button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
                  <Badge>mode: {t.executionMode}</Badge>
                  <Badge variant="secondary">review: {t.reviewMode}</Badge>
                  <Badge variant="outline">confidence ≥ {t.confidenceThreshold}</Badge>
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {stagesFor(t).map((s) => (
                    <Badge key={s} variant="outline">{s}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="h-fit rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="text-sm font-semibold">{editingId ? 'Edit workflow type' : 'New workflow type'}</h2>
            <Input className="mt-2" placeholder="key (e.g. default, refund)" value={key} disabled={!!editingId} onChange={(e) => setKey(e.target.value)} />
            <Input className="mt-2" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <label className="mt-3 block text-xs text-gray-500">Execution mode</label>
            <Segments value={executionMode} options={EXECUTION_MODES} onChange={setExecutionMode} />
            <label className="mt-3 block text-xs text-gray-500">Review mode</label>
            <Segments value={reviewMode} options={REVIEW_MODES} onChange={setReviewMode} />
            <label className="mt-3 block text-xs text-gray-500">Confidence threshold</label>
            <Input
              className="mt-1"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={confidenceThreshold}
              onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
            />
            <label className="mt-3 block text-xs text-gray-500">Enabled stages</label>
            <div className="mt-1 grid grid-cols-2 gap-1">
              {STAGES.map((s) => (
                <label key={s} className="flex items-center gap-2 py-0.5 text-xs">
                  <input type="checkbox" checked={isEnabled(s)} disabled={isAlwaysOn(s)} onChange={() => toggle(s)} />
                  {s}
                </label>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              {editingId && <Button className="flex-1" size="sm" variant="ghost" onClick={resetForm}>Cancel</Button>}
              <Button className="flex-1" size="sm" disabled={!key.trim() || !name.trim() || saving} onClick={() => void save()}>
                {saving ? 'Saving...' : editingId ? 'Save' : 'Add type'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={pendingId !== null} onOpenChange={(open) => { if (!open) setPendingId(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete workflow type</DialogTitle></DialogHeader>
          <DialogBody><p className="text-sm">Delete this workflow type?</p></DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingId(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!pendingId} onClick={() => pendingId && void remove(pendingId)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
